import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import MetaTag from '../../layout/MetaTag';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import swal from 'sweetalert';
import './confirmOrder.css';
import CheckoutStep from './CheckoutStep';
import Loader from '../../layout/loader';

import { allcartItemAction } from '../../redux/actions/cartAction';
import { clearErrorAction, applyCoupanAction } from '../../redux/actions/coupanAction';

function ConfirmOrders() {
    const history = useHistory();
    const dispatch = useDispatch();

    // shipping info
    const { shippingInfo } = useSelector((state) => state.shippingInfo);

    // cart items
    const { carts } = useSelector((state) => state.carts);

    const { error, loading, success, coupanCode, coupanValue } = useSelector((state) => state.coupanApply);

    // coupon input
    const [applyCoupan, setApplyCoupan] = useState('');

    // subtotal calculation
    const subTotal = carts ? carts.reduce(
        (acc, item) => acc + Number(item.quantity || 1) * Number(item.price || 0), 0
    ) : 0;

    let itemPrice = 0;
    if (carts && carts.length > 0) {
        itemPrice = Number(carts[0].price || 0);
    }

    // quantity calculation
    const shippingQuantity = carts ? carts.reduce(
        (acc, item) => acc + Number(item.quantity || 1), 0
    ) : 0;

    // Shipping charges:
    // If vendor set shipping charges > 0 (and not marked free delivery), charge that amount;
    // OTHERWISE shipping is 0 (FREE Delivery).
    const shippingCharges = carts ? carts.reduce(
        (acc, item) => {
            const isFree = item.isdeliveryFree === true || item.isDeliveryFree === true;
            if (isFree) return acc;
            const fee = (item.productShippingCharges !== undefined && item.productShippingCharges !== null && Number(item.productShippingCharges) >= 0)
                ? Number(item.productShippingCharges)
                : 0;
            return acc + Number(item.quantity || 1) * fee;
        }, 0
    ) : 0;

    // Box charges (optional premium box packaging)
    const boxCharges = carts ? carts.reduce(
        (acc, item) => acc + Number(item.quantity || 1) * Number(item.selectBox || 0), 0
    ) : 0;

    // GST Tax:
    // Only calculate and show GST if the seller/vendor explicitly configured GST
    const totalGstTax = carts ? carts.reduce(
        (acc, item) => {
            if (item.isGstApplicable === true && Number(item.gstPercent || 0) > 0) {
                return acc + Math.round((Number(item.price || 0) * Number(item.quantity || 1) * Number(item.gstPercent)) / 100);
            }
            return acc;
        }, 0
    ) : 0;

    const roundOffTotalTax = Math.round(totalGstTax);

    // Total price
    const totalPrice = subTotal + roundOffTotalTax + shippingCharges + boxCharges;

    const address = `${shippingInfo.address}, ${shippingInfo.landMark ? `${shippingInfo.landMark}, ` : ''}${shippingInfo.city}, ${shippingInfo.state}, ${shippingInfo.district ? `${shippingInfo.district}, ` : ''}${shippingInfo.pincode}`;

    const proccedToPayments = () => {
        const data = {
            itemPrice,
            subTotal,
            shippingCharges,
            roundOffTotalTax,
            totalPrice,
            coupanCode,
            coupanValue,
            boxCharges,
            carts
        };

        sessionStorage.setItem('orderInfo', JSON.stringify(data));
        history.push('/confirm/payments');
    };

    const coupanApplySubmitHandler = (e) => {
        e.preventDefault();
        if (!applyCoupan.trim()) {
            return swal({
                title: "Coupon Required",
                text: "Please enter a valid coupon code.",
                icon: "info",
            });
        }
        dispatch(applyCoupanAction(applyCoupan.trim().toUpperCase(), totalPrice));
    };

    useEffect(() => {
        if (error) {
            swal({
                title: 'Coupon Error',
                text: error,
                icon: 'error'
            });
            dispatch(clearErrorAction());
        }
        if (success) {
            swal({
                title: 'Coupon Applied!',
                text: `Coupon ${coupanCode} applied successfully.`,
                icon: 'success'
            });
        }

        dispatch(allcartItemAction());
    }, [dispatch, error, success, coupanCode]);

    return (
        <Fragment>
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    <MetaTag title="Review & Confirm Your Order | IM Online Shopping" />
                    <CheckoutStep activeStep={1} />

                    <div className="confirm-order-page">
                        {/* LEFT COLUMN: Shipping details & Order Items */}
                        <div className="confirm-left-column">
                            {/* Shipping Information Card */}
                            <div className="confirm-card confirm-shipping-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <LocationOnOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Shipping Address</h3>
                                        <p>Your order will be delivered to this verified location</p>
                                    </div>
                                    <Link to="/Checkout" className="confirm-edit-link">
                                        Change
                                    </Link>
                                </div>

                                <div className="shipping-info-grid">
                                    <div className="shipping-info-row">
                                        <span className="label">Recipient:</span>
                                        <span className="value"><strong>{shippingInfo.name}</strong></span>
                                    </div>
                                    <div className="shipping-info-row">
                                        <span className="label">Contact:</span>
                                        <span className="value">{shippingInfo.phoneNo} {shippingInfo.email ? `• ${shippingInfo.email}` : ''}</span>
                                    </div>
                                    <div className="shipping-info-row">
                                        <span className="label">Delivery Address:</span>
                                        <span className="value address-text">{address}</span>
                                    </div>
                                </div>

                                {/* Delivery & COD reassurance strip */}
                                <div className="shipping-cod-strip">
                                    <div className="cod-badge">
                                        <CheckCircleOutlineIcon style={{ fontSize: '18px', color: '#16a34a' }} />
                                        <span>Cash on Delivery Available</span>
                                    </div>
                                    <div className="shipping-speed-badge">
                                        <LocalShippingOutlinedIcon style={{ fontSize: '18px', color: '#1d4ed8' }} />
                                        <span>Standard Doorstep Delivery</span>
                                    </div>
                                </div>
                            </div>

                            {/* Cart Items Card */}
                            <div className="confirm-card confirm-items-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <ShoppingBagOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Order Items ({shippingQuantity})</h3>
                                        <p>Review the products in your shopping bag before payment</p>
                                    </div>
                                </div>

                                <div className="confirm-cartItems-container">
                                    {carts && carts.map((item, index) => (
                                        <div key={item.product || index} className="confirm-item-row">
                                            <div className="confirm-item-img-wrap">
                                                <img src={item.image} alt={item.name} />
                                            </div>

                                            <div className="confirm-item-info">
                                                <Link to={`/ProductDetail/${item.product}`} className="confirm-item-title">
                                                    {item.name}
                                                </Link>
                                                <div className="confirm-item-specs">
                                                    {item.size && <span className="spec-pill">Size: {item.size}</span>}
                                                    {item.colour && <span className="spec-pill">Color: {item.colour}</span>}
                                                    <span className="spec-pill cod-pill">✓ COD Eligible</span>
                                                </div>
                                                <div className="confirm-item-price-calc">
                                                    <span className="calc-qty">{item.quantity} × ₹{item.price}</span>
                                                    <span className="calc-total">
                                                        <CurrencyRupeeIcon style={{ fontSize: '14px' }} />
                                                        {Number(item.quantity || 1) * Number(item.price || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Order Summary & Coupon */}
                        <div className="confirm-right-column">
                            <div className="confirm-card order-summary-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <PaymentsOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Order Summary</h3>
                                        <p>Price breakdown for this order</p>
                                    </div>
                                </div>

                                <div className="summary-rows-group">
                                    <div className="summary-row">
                                        <span className="summary-label">Items Subtotal</span>
                                        <span className="summary-value">₹{subTotal}</span>
                                    </div>

                                    <div className="summary-row">
                                        <span className="summary-label">Shipping Charges</span>
                                        {shippingCharges === 0 ? (
                                            <span className="summary-value free-tag">FREE Delivery</span>
                                        ) : (
                                            <span className="summary-value">₹{shippingCharges}</span>
                                        )}
                                    </div>

                                    {boxCharges > 0 && (
                                        <div className="summary-row">
                                            <span className="summary-label">Packaging / Box</span>
                                            <span className="summary-value">₹{boxCharges}</span>
                                        </div>
                                    )}

                                    {/* GST is ONLY displayed when the seller explicitly configured it */}
                                    {roundOffTotalTax > 0 && (
                                        <div className="summary-row">
                                            <span className="summary-label">GST Tax</span>
                                            <span className="summary-value">₹{roundOffTotalTax}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="summary-total-row">
                                    <div>
                                        <strong>Total Amount</strong>
                                        <span className="tax-inclusive-note">
                                            {roundOffTotalTax > 0 ? 'Includes all applicable taxes' : 'All taxes included'}
                                        </span>
                                    </div>
                                    <div className="total-amount-display">
                                        <CurrencyRupeeIcon style={{ fontSize: '20px' }} />
                                        <span>{totalPrice}</span>
                                    </div>
                                </div>

                                {/* Coupon Section */}
                                <div className="coupon-box">
                                    <div className="coupon-title">Have a coupon or promo code?</div>
                                    <form onSubmit={coupanApplySubmitHandler} className="coupon-input-group">
                                        <input
                                            type="text"
                                            className="coupon-input"
                                            placeholder="Enter coupon code"
                                            value={applyCoupan}
                                            onChange={(e) => setApplyCoupan(e.target.value)}
                                        />
                                        <button type="submit" className="coupon-apply-btn">
                                            Apply
                                        </button>
                                    </form>

                                    {success && coupanCode && (
                                        <div className="coupon-applied-badge">
                                            <div>
                                                <strong>{coupanCode} Applied!</strong>
                                                <span>Discount value: ₹{coupanValue}</span>
                                            </div>
                                            <span className="coupon-check">✓</span>
                                        </div>
                                    )}
                                </div>

                                {/* Payment Call to Action */}
                                <button className="proceed-to-payment-btn" onClick={proccedToPayments}>
                                    Proceed to Payment &rarr;
                                </button>

                                {/* Trust & Guarantee icons */}
                                <div className="confirm-trust-strip">
                                    <div className="trust-item">
                                        <VerifiedUserOutlinedIcon style={{ fontSize: '16px', color: '#16a34a' }} />
                                        <span>100% Safe Checkout</span>
                                    </div>
                                    <div className="trust-item">
                                        <CheckCircleOutlineIcon style={{ fontSize: '16px', color: '#16a34a' }} />
                                        <span>Cash on Delivery Option</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
}

export default ConfirmOrders;
